/**
 * Turns a pasted list of site names into the catalogue names they refer to.
 *
 * Accepts English catalogue names and the German client names in SITE_ALIASES,
 * in any order: one name per line, or a tab-separated scanner table where the
 * name can sit in any column (every cell is tried). Matching ignores case,
 * accents, hyphens and repeated spaces.
 */

/** German client name -> English catalogue name. Only entries with a source go
 * here; a translation invented from memory would silently never match the real
 * client text, so it would look like a bug rather than a gap.
 *
 * Source: EVE Sister Core Scanner Probe Wiki (sistersprobe.fandom.com, de),
 * which lists Forgotten Frontier Quarantine Outpost under this German title.
 * Not yet checked against the German client's own strings. */
export const SITE_ALIASES: Record<string, string> = {
  'Vergessener Quarantäne-Außenposten im Grenzgebiet': 'Forgotten Frontier Quarantine Outpost',
};

export function normalizeSiteName(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/\p{M}/gu, '') // strip accents: ä -> a, é -> e
    .replace(/ß/g, 'ss')
    .replace(/[-‐‑–—]/g, ' ') // hyphens and dashes read as spaces
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export interface SitePasteResult {
  /** Catalogue names (from `names`) the paste refers to. */
  matched: Set<string>;
  /** Lines that matched nothing on this page, as pasted. */
  unmatched: string[];
}

/** `names` is the set of catalogue names on the page. A German name whose
 * English site is not on the page is reported as unmatched, not silently
 * matched to nothing, so the count the user sees is honest. */
export function parseSitePaste(text: string, names: string[]): SitePasteResult {
  const onPage = new Set(names);
  const byKey = new Map<string, string>();
  for (const name of names) byKey.set(normalizeSiteName(name), name);
  for (const [german, english] of Object.entries(SITE_ALIASES)) {
    if (onPage.has(english)) byKey.set(normalizeSiteName(german), english);
  }

  const matched = new Set<string>();
  const unmatched: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const cells = line
      .split('\t')
      .map((cell) => cell.trim())
      .filter(Boolean);
    if (cells.length === 0) continue;
    const hits = cells.map((cell) => byKey.get(normalizeSiteName(cell))).filter((name): name is string => !!name);
    if (hits.length === 0) unmatched.push(line.trim());
    for (const hit of hits) matched.add(hit);
  }
  return { matched, unmatched };
}
